// Standalone session-teardown helpers.
//
// This module deliberately depends on neither `authContext` nor `useApi` so it can
// be imported by both without creating a circular dependency (`authContext` imports
// `useApi`, so `useApi` cannot import `authContext`).

import { toast } from "react-toastify";

// localStorage key for the persisted user details. Owned here so both the auth
// context and the API layer reference the same constant.
export const USER_STORAGE_KEY = "curate_user";

// Latches "this tab has already torn down a dead session". sessionStorage because the
// latch has to survive the redirect below and die with the tab.
const TEARDOWN_LATCH_KEY = "curate_session_teardown";

/**
 * Clear the teardown latch. Called when the backend confirms a live session, so a later
 * expiry in the same tab gets its redirect.
 */
export function markSessionAlive() {
  sessionStorage.removeItem(TEARDOWN_LATCH_KEY);
}

/**
 * Clear client-side session state and hard-redirect to the logged-out home.
 *
 * Used whenever the session is already gone (a failed token refresh, or the auth
 * check detecting a dead session) or must be ended regardless of the backend
 * (account deletion). It performs no network call — server-side cookie clearing,
 * where relevant, is the caller's concern.
 *
 * The redirect fires at most once per tab, and never from the landing page: its only job
 * is to move the user off a page they can no longer load, so where there is nothing to
 * move them off, the app renders its logged-out state in place. The bound also stops a
 * still-detectable session from cycling the tab.
 *
 * @param {string} [message] - message to show the user about the sign-out.
 * @param {string} [type="success"] - message severity ("success" | "info" | ...).
 */
export function clearLocalSession(message, type = "success") {
  localStorage.removeItem(USER_STORAGE_KEY);

  const alreadyToreDown = sessionStorage.getItem(TEARDOWN_LATCH_KEY) === "true";
  sessionStorage.setItem(TEARDOWN_LATCH_KEY, "true");

  if (alreadyToreDown || window.location.pathname === "/") {
    // Nothing will reload, so show the message here rather than stashing it for a
    // landing page that is already mounted.
    if (message) toast[type](message);
    return;
  }

  // Stashed in localStorage for the landing page to surface after the redirect.
  if (message) {
    localStorage.setItem("flashMessage", message);
    localStorage.setItem("flashType", type);
  }

  // Hard navigation tears down the React tree and any in-flight queries, so we
  // don't need to reset React state for the redirect to be clean.
  window.location.href = "/";
}
